import semver from "semver";
import { fetchMinVersion } from "@ano/shared/api/min-version";

export interface MinCliVersionViolation {
  currentVersion: string;
  minVersion: string;
  upgradeCommand: string;
}

export interface MinCliVersionCheckInput {
  apiBaseUrl: string;
  currentVersion: string | null;
}

/**
 * Fetches `/api/min-version` and compares against the locally installed
 * `@ano-chat/cli` version. Mirrors the desktop force-update gate, but
 * informational rather than blocking — the user keeps using the app, we
 * just nudge them to upgrade their CLI so the Shell-based automation
 * flow has the latest tools (`webhook-setup`, `create-compiled`, etc.).
 *
 * Fails open on any error (network, 5xx, malformed response, missing
 * CLI) so a server hiccup or a fresh install with no `ano` on PATH
 * never blocks the app.
 *
 * `currentVersion === null` means the CLI isn't installed at all — we
 * surface that as a violation so the banner can prompt installation
 * (only when `minCliVersion > 0.0.0`).
 */
export async function checkMinCliVersion({
  apiBaseUrl,
  currentVersion,
}: MinCliVersionCheckInput): Promise<MinCliVersionViolation | null> {
  try {
    const response = await fetchMinVersion({ apiBaseUrl });
    const min = response.minCliVersion;
    if (!semver.valid(min) || semver.eq(min, "0.0.0")) {
      // Min not enforced.
      return null;
    }
    const upgradeCommand =
      response.cliUpgradeCommand?.trim() ||
      "npm install -g @ano-chat/cli@latest";
    if (currentVersion === null) {
      return {
        currentVersion: "(not installed)",
        minVersion: min,
        upgradeCommand,
      };
    }
    if (!semver.valid(currentVersion)) {
      // Unrecognizable version string — fail open rather than nag the user
      // about a malformed `ano --version` output.
      return null;
    }
    if (semver.lt(currentVersion, min)) {
      return {
        currentVersion,
        minVersion: min,
        upgradeCommand,
      };
    }
    return null;
  } catch (error) {
    console.warn("[min-cli-version] check failed — failing open", error);
    return null;
  }
}
